import React, { useMemo } from 'react';

import type * as api from '@gitbook/api-client';
import { assertNever, filterOutNullable } from '@gitbook/util-ts';

import { flattenGroupedSections } from '../../state';

/**
 * Get the sites of an organization.
 * It's designed to match the `listSites` API, while using the optimized `getOrganizationAllContent` API that is prefetched by the sidebar.
 */
export function useOrganizationSites(
    data: api.OrganizationAllContent | null,
    filter: {
        /** Filter by published status */
        published?: boolean;

        /** Filter by title */
        title?: string;

        /** Filter by space */
        spaceId?: string;
    } = {}
): api.OrganizationAllSite[] {
    return React.useMemo(() => {
        if (!data) {
            return [];
        }

        const allSites = data.sites;

        return allSites.filter((item) => {
            if (filter.published !== undefined && item.site.published !== filter.published) {
                return false;
            }

            if (
                filter.title &&
                !item.site.title.toLowerCase().includes(filter.title.toLowerCase())
            ) {
                return false;
            }

            if (filter.spaceId) {
                return !!getSpaceInSiteStructure(item.structure, filter.spaceId);
            }

            return true;
        });
    }, [data, filter.published, filter.title, filter.spaceId]);
}

/**
 * Get the ancestors of a content in an organization.
 */
export function useOrganizationContentItem(
    data: api.OrganizationAllContent,
    content: { collection: string; space?: undefined }
): OrgAllContentItem<api.OrganizationAllCollection> | undefined;
export function useOrganizationContentItem(
    data: api.OrganizationAllContent,
    content: { space: string; collection?: undefined }
): OrgAllContentItem<api.OrganizationAllSpace> | undefined;
export function useOrganizationContentItem(
    data: api.OrganizationAllContent,
    content: { collection: string; space?: undefined } | { space: string; collection?: undefined }
): OrgAllContentItem<api.OrganizationAllCollection | api.OrganizationAllSpace> | undefined {
    return React.useMemo(() => {
        if (content.collection) {
            return getCollectionFromOrgAllContent(data, content.collection);
        }

        if (content.space) {
            return getSpaceFromOrgAllContent(data, content.space);
        }

        return;
    }, [data, content.collection, content.space]);
}

/**
 * Get all the sites linked to a space.
 */
export function useSpaceSites(
    organizationContent: api.OrganizationAllContent | null,
    spaceId: string
) {
    const sites = useOrganizationSites(organizationContent, { spaceId });

    return React.useMemo(() => {
        return sites
            .map((entry) => {
                const siteSpace = getSpaceInSiteStructure(entry.structure, spaceId);
                if (!siteSpace) {
                    return null;
                }

                return {
                    site: entry.site,
                    siteSpace,
                };
            })
            .filter(filterOutNullable);
    }, [sites, spaceId]);
}

export interface SpacePrimarySiteLookup {
    site: api.Site;
    siteSpace: api.SiteSpace;
}

/**
 * Get the primary site linked to a space.
 * It prioritizes published sites.
 */
export function useSpacePrimarySite(
    organizationContent: api.OrganizationAllContent | null,
    spaceId: string
): SpacePrimarySiteLookup | null {
    const sites = useSpaceSites(organizationContent, spaceId);
    return useMemo(() => sites.find((site) => site.site.published) ?? sites[0] ?? null, [sites]);
}

/**
 * Find the first space in the content of an organization.
 */
export function getFirstSpaceInOrgAllContent(
    allContent: api.OrganizationAllContent
): api.OrganizationAllSpace | null {
    const iterate = (items: Array<api.OrganizationAllCollection | api.OrganizationAllSpace>) => {
        for (const item of items) {
            if ('space' in item) {
                return item;
            }

            const result = iterate(item.content);
            if (result) {
                return result;
            }
        }

        return null;
    };

    return iterate(allContent.spaces) || null;
}

/**
 * Flatten the list of spaces in the content of an organization.
 */
export function flattenSpacesInOrgAllContent<T = api.Space>(
    allContent: api.OrganizationAllContent,
    map: (space: api.OrganizationAllSpace) => T | null = (item) => item.space as T
): T[] {
    const iterate = (items: Array<api.OrganizationAllCollection | api.OrganizationAllSpace>) => {
        const spaces: T[] = [];
        for (const item of items) {
            if ('space' in item) {
                const mapped = map(item);
                if (mapped) {
                    spaces.push(mapped);
                }
            } else {
                spaces.push(...iterate(item.content));
            }
        }

        return spaces;
    };

    return iterate(allContent.spaces);
}

/**
 * Get a site item from the OrganizationAllContent.
 */
export function getSiteFromOrgAllContent(
    allContent: api.OrganizationAllContent,
    siteId: string
): OrgAllContentItem<api.OrganizationAllSite> | undefined {
    const { sites } = getIndexesFromOrgAllContent(allContent);
    return sites.get(siteId);
}

/**
 * Get a collection item from the OrganizationAllContent.
 */
export function getCollectionFromOrgAllContent(
    allContent: api.OrganizationAllContent,
    collectionId: string
): OrgAllContentItem<api.OrganizationAllCollection> | undefined {
    const { collections } = getIndexesFromOrgAllContent(allContent);
    return collections.get(collectionId);
}

/**
 * Get a space item from the OrganizationAllContent.
 */
export function getSpaceFromOrgAllContent(
    allContent: api.OrganizationAllContent,
    spaceId: string
): OrgAllContentItem<api.OrganizationAllSpace> | undefined {
    const { spaces } = getIndexesFromOrgAllContent(allContent);
    return spaces.get(spaceId);
}

/**
 * Get a site space from a structure by its space ID.
 */
export function getSpaceInSiteStructure(
    structure: api.SiteStructure,
    spaceId: string
): api.SiteSpace | undefined {
    if (structure.type === 'siteSpaces') {
        return structure.structure.find((siteSpace) => siteSpace.space.id === spaceId);
    }

    for (const section of flattenGroupedSections(structure.structure)) {
        const result = section.siteSpaces.find((siteSpace) => siteSpace.space.id === spaceId);
        if (result) {
            return result;
        }
    }

    return;
}

/**
 * Get a site section from a structure by its ID.
 */
export function getSiteSectionInSiteStructure(
    structure: api.SiteStructure,
    sectionId: string
): api.SiteSection | null {
    switch (structure.type) {
        case 'siteSpaces':
            return null;
        case 'sections':
            return (
                flattenGroupedSections(structure.structure).find(
                    (section) => section.id === sectionId
                ) ?? null
            );
        default:
            assertNever(structure);
    }
}

/**
 * Get the siblings site spaces of a site space in a structure.
 */
export function getSiteSpaceSiblingsInSiteStructure(
    structure: api.SiteStructure,
    siteSpaceId: string
): api.SiteSpace[] {
    if (structure.type === 'siteSpaces') {
        return structure.structure.filter((siteSpace) => siteSpace.id !== siteSpaceId);
    }

    for (const section of flattenGroupedSections(structure.structure)) {
        const result = section.siteSpaces.filter((siteSpace) => siteSpace.id !== siteSpaceId);
        if (result) {
            return result;
        }
    }

    return [];
}

/**
 * Get a site space from a structure by its ID.
 */
export function getSiteSpaceInSiteStructure(
    structure: api.SiteStructure,
    siteSpaceId: string
): api.SiteSpace | undefined {
    if (structure.type === 'siteSpaces') {
        return structure.structure.find((siteSpace) => siteSpace.id === siteSpaceId);
    }

    for (const section of flattenGroupedSections(structure.structure)) {
        const result = section.siteSpaces.find((siteSpace) => siteSpace.id === siteSpaceId);
        if (result) {
            return result;
        }
    }

    return;
}

/**
 * Get the default site space for a site.
 */
export function getDefaultSiteSpaceInSiteStructure(
    structure: api.SiteStructure
): api.SiteSpace | undefined {
    if (structure.type === 'siteSpaces') {
        return structure.structure.find((siteSpace) => siteSpace.default);
    }

    for (const section of flattenGroupedSections(structure.structure)) {
        if (section.default) {
            const result = section.siteSpaces.find((siteSpace) => siteSpace.default);
            if (result) {
                return result;
            }
        }
    }

    return;
}

export type OrgAllContentItem<T> = { item: T; ancestors: api.OrganizationAllCollection[] };
export type OrgAllContentItemsIndex<T> = Map<string, OrgAllContentItem<T>>;

const cache = new WeakMap<api.OrganizationAllContent, ReturnType<typeof indexContentItems>>();

/**
 * Compute and cache indexes of spaces/collections from an OrganizationAllContent.
 */
export function getIndexesFromOrgAllContent(content: api.OrganizationAllContent) {
    const existing = cache.get(content);

    if (existing) {
        return existing;
    }

    const indexes = indexContentItems(content);
    cache.set(content, indexes);
    return indexes;
}

function indexContentItems(content: api.OrganizationAllContent) {
    const sites: OrgAllContentItemsIndex<api.OrganizationAllSite> = new Map();
    const spaces: OrgAllContentItemsIndex<api.OrganizationAllSpace> = new Map();
    const collections: OrgAllContentItemsIndex<api.OrganizationAllCollection> = new Map();

    const indexLevel = (
        items: Array<
            api.OrganizationAllSite | api.OrganizationAllCollection | api.OrganizationAllSpace
        >,
        ancestors: api.OrganizationAllCollection[] = []
    ) => {
        for (const item of items) {
            if ('site' in item) {
                sites.set(item.site.id, { item, ancestors });
            } else if ('space' in item) {
                spaces.set(item.space.id, { item, ancestors });
            } else {
                collections.set(item.collection.id, { item, ancestors });
                indexLevel(item.content, [...ancestors, item]);
            }
        }
    };

    indexLevel(content.sites);
    indexLevel(content.spaces);

    return { sites, spaces, collections };
}
